import { useLocation } from 'react-router-dom';
import { ArrowRight, BookOpenText, Calculator, ClipboardList, FileText, Info, ShieldCheck } from 'lucide-react';
import { PageStructuredData } from '@/components/seo/PageStructuredData';
import { StructuredData } from '@/components/seo/StructuredData';
import { CheckList, CtaPanel, DataTable, FaqSection, HeroActions, InShort, PageHero, PageShell, RelatedLinks, Reveal, Section, SectionHeading, SmartLink, StepFlow } from '@/components/page/PageKit';
import { Button } from '@/components/ui/button';
import { APP_URL } from '@/data/marketingContent';
import { agencyStartGuideUrl, getAgencyStartGuide, type AgencyStartGuide, type GuideSection } from '@/data/agencyStartGuides';
import { articleSchema, faqSchema } from '@/seo/schemas';
import { useSEO } from '@/hooks/useSEO';
import NotFound from '@/pages/NotFound';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const formatDate = (iso: string) => {
  const [year, month, day] = iso.split('-').map(Number);
  return `${day} ${MONTHS[month - 1]} ${year}`;
};

const TONES = ['white', 'base'] as const;

function SourceLinks({ sources }: { sources: GuideSection['sources'] }) {
  if (!sources?.length) return null;
  return (
    <div className="mt-6 rounded-2xl border border-bv-blue-line bg-white p-4 text-[13.5px] leading-6 text-bv-ink2">
      <span className="font-semibold text-bv-ink">Sources: </span>
      {sources.map((s, i) => (
        <span key={s.url}>{i > 0 && ' · '}<a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="text-bv-blue hover:underline">{s.label}</a></span>
      ))}
    </div>
  );
}

function GuideBlock({ section, tone }: { section: GuideSection; tone: (typeof TONES)[number] }) {
  return (
    <Section tone={tone} id={section.id} labelledBy={`${section.id}-title`}>
      <div className="mx-auto max-w-4xl">
        <SectionHeading id={`${section.id}-title`} eyebrow={section.eyebrow} title={section.title} />
        <Reveal delay={0.05}>
          <div className="mt-6 space-y-4 text-[16.5px] leading-8 text-bv-ink2">
            {section.paragraphs.map(p => <p key={p.slice(0, 48)}>{p}</p>)}
          </div>
          {section.bullets && <CheckList className="mt-6" items={section.bullets} />}
        </Reveal>
        {section.table && (
          <Reveal delay={0.1} className="mt-8">
            <DataTable caption={section.table.caption} columns={section.table.columns} rows={section.table.rows} minWidth={section.table.minWidth ?? 640} />
          </Reveal>
        )}
        {section.callout && (
          <Reveal delay={0.1}>
            <div className="mt-6 flex gap-3 rounded-2xl border border-bv-blue-line bg-bv-blue-wash/60 p-5 text-[15px] leading-7 text-bv-ink2">
              <ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-bv-blue" />
              <div><p className="font-semibold text-bv-ink">{section.callout.title}</p><p className="mt-1">{section.callout.body}</p></div>
            </div>
          </Reveal>
        )}
        <SourceLinks sources={section.sources} />
      </div>
    </Section>
  );
}

function GuidePage({ g }: { g: AgencyStartGuide }) {
  const url = agencyStartGuideUrl(g.slug);
  const headline = `${g.h1[0]} ${g.h1[1]}`;
  const other = g.country === 'US'
    ? { title: 'Starting in the UK?', body: 'Licences, the Conduct Regulations, HMRC and the 2026 rule changes.', href: '/guides/how-to-start-a-recruitment-agency-uk' }
    : { title: 'Starting in the US?', body: 'State licensing, payroll taxes, Form I-9 and joint employment.', href: '/guides/how-to-start-a-staffing-agency' };
  useSEO({ title: g.seoTitle, description: g.description, canonicalUrl: url, ogType: 'article' });

  return (
    <PageShell>
      <PageStructuredData name={headline} description={g.description} url={url} dateModified={g.reviewed} breadcrumbs={[{ name: 'Resources', url: 'https://beatview.ai/resources' }, { name: headline }]} />
      <StructuredData data={[articleSchema({ headline, description: g.description, url, datePublished: g.published, dateModified: g.reviewed }), faqSchema(g.faqs)]} />
      <article>
        <PageHero breadcrumbs={[{ name: 'Resources', href: '/resources' }, { name: g.crumb ?? (g.country === 'US' ? 'Start a staffing agency' : 'Start a recruitment agency (UK)') }]} eyebrow={`Guide · Checked ${formatDate(g.reviewed)}`} title={<>{g.h1[0]} <span className="bv-hero-highlight">{g.h1[1]}</span></>}>
          <InShort label="The short answer">{g.shortAnswer}</InShort>
          <div className="mt-7"><HeroActions primaryLabel="Get the free business plan template" primaryHref="/templates/staffing-agency-business-plan" secondary={g.glance ? { label: g.glanceTitle ? 'See the summary' : 'See the steps', href: '#at-a-glance' } : undefined} note={g.note ?? `Every legal, tax and fee figure links to an official source, read on ${formatDate(g.reviewed)}.`} /></div>
        </PageHero>

        {g.glance && (
          <Section tone="wash" id="at-a-glance" labelledBy="glance-title">
            <SectionHeading id="glance-title" eyebrow="At a glance" title={g.glanceTitle ?? (g.country === 'US' ? 'Starting a staffing agency, step by step' : 'Starting a UK recruitment agency, step by step')} description={g.glanceTitle ? 'A summary of what follows. Each row is explained below with its source.' : 'The official steps in order. Each is explained below with its source.'} />
            <Reveal delay={0.1} className="mt-10">
              <DataTable caption={g.glanceTitle ?? 'Steps to start an agency, with where to do each and the official cost'} columns={g.glanceColumns ?? ['Step', 'What to do', 'Where', 'Official cost']} rows={g.glance.map(r => [<span key={r.step} className="font-semibold text-bv-ink">{r.step}</span>, r.what, r.where, r.cost])} minWidth={860} />
            </Reveal>
          </Section>
        )}

        {g.sections.map((section, i) => <GuideBlock key={section.id} section={section} tone={TONES[i % 2]} />)}

        {g.launch && <Section tone="wash" labelledBy="launch-title">
          <SectionHeading id="launch-title" eyebrow="Your first 90 days" title="From registered company to first placement" description="The practical work that wins the first clients, once the legal set-up is under way." />
          <div className="mt-12"><StepFlow steps={g.launch} /></div>
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-[15px] font-semibold">
            <SmartLink href="/templates/staffing-agency-business-plan" className="inline-flex items-center gap-1 text-bv-blue hover:underline">Business plan template <ArrowRight className="h-4 w-4" /></SmartLink>
            <SmartLink href="/guides/recruitment-agency-software" className="inline-flex items-center gap-1 text-bv-blue hover:underline">Recruitment agency software compared <ArrowRight className="h-4 w-4" /></SmartLink>
            <SmartLink href="/templates/phone-screening-questions" className="inline-flex items-center gap-1 text-bv-blue hover:underline">Phone screening questions <ArrowRight className="h-4 w-4" /></SmartLink>
            <SmartLink href="/guides/recruitment-agency-fees" className="inline-flex items-center gap-1 text-bv-blue hover:underline">What agencies charge <ArrowRight className="h-4 w-4" /></SmartLink>
          </div>
        </Section>}

        <Section tone="dark" id="beatview" labelledBy="beatview-title">
          <SectionHeading tone="dark" id="beatview-title" eyebrow="Where Beatview fits" title={<>{g.beatviewTitle?.[0] ?? 'Screen every applicant'} <span className="bv-hero-highlight">{g.beatviewTitle?.[1] ?? 'without hiring a team first.'}</span></>} description={g.beatview} />
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="bv-shine h-[54px] rounded-xl px-7 text-base font-semibold"><SmartLink href={APP_URL}>Start for free <ArrowRight className="h-4 w-4" /></SmartLink></Button>
            <Button asChild size="lg" variant="outline" className="h-[54px] rounded-xl border-white/20 bg-white/5 px-7 text-base font-semibold text-white hover:bg-white/10 hover:text-white"><SmartLink href="/for-recruiting-agencies">How agencies use Beatview</SmartLink></Button>
          </div>
        </Section>

        <FaqSection title={g.faqTitle ?? 'Starting an agency: frequently asked questions'} items={g.faqs} idPrefix={`${g.slug}-faq`} tone="base" />

        <Section tone="white" narrow labelledBy="disclaimer-title">
          <h2 id="disclaimer-title" className="sr-only">About this guide</h2>
          <div className="flex gap-3 rounded-2xl border border-bv-blue-line bg-bv-base p-5 text-[14px] leading-6 text-bv-ink2">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-bv-blue" />
            <p>{g.disclaimer} Beatview publishes this guide and sells screening software to recruitment agencies. Spotted something out of date? <SmartLink href="/contact" className="font-semibold text-bv-blue hover:underline">Tell us</SmartLink>.</p>
          </div>
        </Section>
      </article>

      <RelatedLinks eyebrow="Keep going" title="Tools for a new agency" tone="base" items={g.related ? g.related.map((r, i) => ({ ...r, icon: [FileText, BookOpenText, Calculator, ClipboardList][i % 4] })) : [
        { title: 'Business plan template', body: 'Niche, pricing, cash flow, break-even and KPIs, ready to fill in.', href: '/templates/staffing-agency-business-plan', icon: FileText },
        { ...other, icon: BookOpenText },
        { title: 'Recruitment software pricing', body: 'What agency ATS and AI tools cost, with published prices.', href: '/guides/recruitment-software-pricing', icon: Calculator },
        { title: 'Free recruitment templates', body: 'Screening questions, rubrics, evaluation forms and reference checks.', href: '/templates', icon: ClipboardList },
      ]} />
      <CtaPanel eyebrow="Start free" title={<>Run your first roles <span className="bv-hero-highlight">with screening built in.</span></>} description="Beatview’s Free plan covers one active job with unlimited candidates. Pro adds AI video interviews." secondary={{ label: 'Compare agency software', href: '/guides/recruitment-agency-software' }} />
    </PageShell>
  );
}

export function AgencyStartGuidePage({ slug }: { slug: string }) {
  const g = getAgencyStartGuide(slug);
  return g ? <GuidePage g={g} /> : <NotFound />;
}

/** Route component: the slug is the last path segment. */
export default function AgencyStartGuideRoute() {
  const { pathname } = useLocation();
  return <AgencyStartGuidePage slug={pathname.replace(/\/$/, '').split('/').pop() ?? ''} />;
}

